import React from "react";
import { Link } from "react-router-dom";
import { Heart, Trash2, MessageCircle, ArrowLeft } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingWhatsApp from "../components/FloatingWhatsApp";
import { useWishlist } from "../context/WishlistContext";
import { useAuth } from "../context/AuthContext";
import { designUrl } from "../data/catalog";
import { useStoreSettings } from "../hooks/useHomeContent";
import { trackWhatsApp } from "../lib/track";

const buildBulkEnquiry = (items, user, waBase) => {
  const lines = items.map(
    (d, i) =>
      `${i + 1}. ${d.name}\n   Ref: ${d.id.toUpperCase()} · ${d.purity} · ${d.weight}${d.weightUnit}`
  );
  const header = `Hi Alankar Jewellers,\n\nThis is ${
    (user && user.name) || "a customer"
  }.\nI'd like to enquire about the following ${items.length} design${
    items.length === 1 ? "" : "s"
  }:\n\n`;
  return `${waBase}?text=${encodeURIComponent(header + lines.join("\n\n"))}`;
};

export default function WishlistPage() {
  const { items, remove, clear } = useWishlist();
  const { user } = useAuth();
  const store = useStoreSettings();

  return (
    <div className="alankar-site">
      <Header />
      <section className="wl-hero">
        <div className="wrap">
          <Link to="/" className="wl-back">
            <ArrowLeft size={16} /> Back to Home
          </Link>
          <span className="eyebrow">Your Wishlist</span>
          <h1>
            {items.length === 0
              ? "Your wishlist is empty."
              : `${items.length} design${items.length === 1 ? "" : "s"} saved.`}
          </h1>
          {items.length > 0 && (
            <div className="wl-actions">
              <a
                className="btn btn-primary"
                target="_top"
                rel="noreferrer"
                href={buildBulkEnquiry(items, user, store.waBase)}
                onClick={() => trackWhatsApp({ designs: items, source: "wishlist-bulk" })}
              >
                <MessageCircle size={16} /> Send all as WhatsApp enquiry
              </a>
              <button type="button" className="btn btn-outline" onClick={clear}>
                <Trash2 size={16} /> Clear Wishlist
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="wl-body">
        <div className="wrap">
          {items.length === 0 ? (
            <div className="wl-empty">
              <div className="wl-empty-icon">
                <Heart size={44} strokeWidth={1.2} />
              </div>
              <p>
                Tap the <Heart size={14} className="inline-heart" /> icon on any
                design to save it here. All your favourites live in one place —
                send them to us on WhatsApp in a single tap.
              </p>
              <Link to="/" className="btn btn-primary">
                Explore designs
              </Link>
            </div>
          ) : (
            <div className="design-grid">
              {items.map((d) => (
                <div key={d.id} className="wl-card">
                  <Link to={designUrl(d)} className="wl-media">
                    <img src={d.image} alt={d.name} loading="lazy" />
                  </Link>
                  <div className="wl-info">
                    <span className="dc-num">{d.number}</span>
                    <h4>
                      <Link to={designUrl(d)}>{d.styleName}</Link>
                    </h4>
                    <p className="dc-meta">
                      {d.subCategory.label} · {d.purity} · {d.weight}
                      {d.weightUnit}
                    </p>
                    <div className="wl-btns">
                      <a
                        target="_top"
                        rel="noreferrer"
                        className="wl-mini wl-mini-primary"
                        href={`${store.waBase}?text=${encodeURIComponent(
                          "Hi Alankar Jewellers, I'd like to know more about: " + d.name
                        )}`}
                        onClick={() => trackWhatsApp({ design: d, source: "wishlist-item" })}
                      >
                        <MessageCircle size={13} /> Enquire
                      </a>
                      <button
                        type="button"
                        className="wl-mini wl-mini-ghost"
                        onClick={() => remove(d.id)}
                      >
                        <Trash2 size={13} /> Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
